// Vector port of TournamentPrintableTable.js's renderGroups(). The
// standings calc / getMatch lookup are duplicated here rather than
// factored out - the same calculation already exists twice in this app
// (TournamentPrintableTable.js's renderGroups and exportUtils.js's
// buildGroupsDataset), so a third copy follows the existing pattern
// instead of a drive-by refactor of the other two.
import { t } from "i18next";
import { PRINT_THEME as T } from "./printTheme";
import { groupCharCode } from "../../../constants";
import { formatPlayerName, formatDoublesPair } from "./exportUtils";
import { px2pt, px2mm, lineHeightMm, drawTableRow, hLine, drawText } from "./pdfKit";

const HEADER_FS = px2pt(11);
const BODY_FS = px2pt(13);
const SCORE_FS = BODY_FS * 1.1;
const CARD_HEADER_FS = px2pt(14);
const HEADER_H = lineHeightMm(HEADER_FS, 1.2) + px2mm(14);
const ROW_H = lineHeightMm(BODY_FS, 1.3) + px2mm(12);
const CARD_HEADER_H = lineHeightMm(CARD_HEADER_FS, 1.2) + px2mm(16);
const CARD_GAP = px2mm(25);
const CARD_RADIUS = px2mm(5);

const NUM_W = px2mm(35);
const PLAYER_COL_W = px2mm(55);
const DIFF_W = px2mm(45);
const PTS_W = px2mm(45);
const PL_W = px2mm(45);
const MIN_NAME_W = px2mm(180);

const groupPlayerCount = (group) => group.number_players || (group.players ? group.players.length : 4);

const getMatch = (group, row, col) => {
    if (row === col) return null;
    const n = groupPlayerCount(group);
    let index = 0;
    for (let r = 0; r < n; r++) {
        for (let c = r + 1; c < n; c++) {
            if ((r === row && c === col) || (r === col && c === row)) return group.matches?.[index];
            index++;
        }
    }
    return null;
};

const computeStandings = (group) => {
    const n = groupPlayerCount(group);
    const stats = Array.from({ length: n }, () => ({ games: 0, points: 0, hasStats: false }));
    let mIndex = 0;
    for (let r = 0; r < n; r++) {
        for (let c = r + 1; c < n; c++) {
            const match = group.matches?.[mIndex++];
            if (match && match.sets && match.sets.length) {
                let g1 = 0, g2 = 0;
                match.sets.forEach(s => { g1 += Number(s.game_score_1) || 0; g2 += Number(s.game_score_2) || 0; });
                stats[r].games += (g1 - g2);
                stats[c].games += (g2 - g1);
                stats[r].points += (match.winner_side === 1 ? 1 : (g1 > g2 ? 1 : 0));
                stats[c].points += (match.winner_side === 2 ? 1 : (g2 > g1 ? 1 : 0));
                stats[r].hasStats = true;
                stats[c].hasStats = true;
            }
        }
    }
    return stats;
};

const groupName = (group, grade, rowIndex) => {
    const p = group.players?.[rowIndex] || group.participants?.[rowIndex];
    if (!p) return "—";
    if (grade === 1) return formatPlayerName(p);
    if (grade === 2) {
        const pair = p.players || p.team?.players || (Array.isArray(p) ? p : []);
        return formatDoublesPair(pair[0], pair[1]);
    }
    return p.name || p.team?.name || `${t("Team")} ${rowIndex + 1}`;
};

export const groupsContentWidthMm = (groups) => {
    const maxPlayers = groups.reduce((m, g) => Math.max(m, groupPlayerCount(g)), 4);
    return NUM_W + MIN_NAME_W + maxPlayers * PLAYER_COL_W + DIFF_W + PTS_W + PL_W;
};

const groupTableHeight = (group) => {
    const n = groupPlayerCount(group);
    return CARD_HEADER_H + HEADER_H + n * ROW_H;
};

export const measureGroupsTables = (data) => {
    const groups = Array.isArray(data) ? data : (data?.groups || []);
    if (!groups.length) return px2mm(40);
    return groups.reduce((sum, g, idx) => sum + groupTableHeight(g) + (idx > 0 ? CARD_GAP : 0), 0);
};

export const drawGroupsTables = (pdf, x, y, data, grade = 1) => {
    const groups = Array.isArray(data) ? data : (data?.groups || []);
    const contentWidthMm = groupsContentWidthMm(groups.length ? groups : [{ number_players: 4 }]);
    let cy = y;

    if (!groups.length) {
        pdf.setFillColor(T.superWhite);
        pdf.rect(x, cy, contentWidthMm, px2mm(40), "F");
        drawText(pdf, t("No data available"), x + contentWidthMm / 2, cy + px2mm(15), { fontSizePt: BODY_FS, color: T.muted, align: "center" });
        return cy + px2mm(40);
    }

    groups.forEach((group, gIdx) => {
        if (gIdx > 0) cy += CARD_GAP;
        const cardTop = cy;
        const n = groupPlayerCount(group);
        const nameW = contentWidthMm - NUM_W - n * PLAYER_COL_W - DIFF_W - PTS_W - PL_W;
        const stats = computeStandings(group);
        const groupLabel = `${t("Group")} ${groupCharCode(group.number)} (${group.number})`;

        // Card header bar - gray/white/uppercase (site's own public-view
        // equivalent fills this green; kept the white-uppercase-on-filled-
        // bar treatment but in a neutral gray tone instead, per user
        // preference).
        pdf.setFillColor(T.headerGray);
        pdf.rect(x, cy, contentWidthMm, CARD_HEADER_H, "F");
        drawText(pdf, groupLabel.toUpperCase(), x + px2mm(12), cy + CARD_HEADER_H / 2 - px2mm(2), { fontSizePt: CARD_HEADER_FS, color: T.white, style: "bold" });
        cy += CARD_HEADER_H;

        // Column header row - plain/uppercase/small, no fill (index.css:
        // `tr.group-header td { font-weight: 400; font-size: 80%;
        // text-transform: uppercase }`, no background-color of its own).
        const headerCols = [
            { widthMm: NUM_W, align: "center", text: "№" },
            { widthMm: nameW, align: "left", text: t("Participants").toUpperCase(), padXMm: px2mm(10) },
            ...Array.from({ length: n }, (_, i) => ({ widthMm: PLAYER_COL_W, align: "center", text: String(i + 1) })),
            { widthMm: DIFF_W, align: "center", text: t("+/-") },
            { widthMm: PTS_W, align: "center", text: t("PTS").toUpperCase() },
            { widthMm: PL_W, align: "center", text: t("Pl").toUpperCase() },
        ];
        drawTableRow(pdf, { x, y: cy, totalWidthMm: contentWidthMm, cols: headerCols, rowHeightMm: HEADER_H, bg: T.superWhite, borderColor: T.border, fontSizePt: HEADER_FS, fontName: pdf.__pdfFontName, textColor: T.ink });
        cy += HEADER_H;

        for (let rowIndex = 0; rowIndex < n; rowIndex++) {
            const p = group.players?.[rowIndex] || group.participants?.[rowIndex];
            const name = groupName(group, grade, rowIndex);
            const diffVal = p?.games != null ? p.games : (stats[rowIndex].hasStats ? stats[rowIndex].games : 0);
            const ptsVal = p?.points != null ? p.points : (stats[rowIndex].hasStats ? stats[rowIndex].points : 0);
            const diff = diffVal !== 0 ? `${diffVal > 0 ? "+" : ""}${diffVal}` : "0";
            const place = p?.place || "—";

            const cols = [
                { widthMm: NUM_W, align: "center", text: rowIndex + 1, color: T.ink },
                { widthMm: nameW, align: "left", text: name, color: p ? T.ink : T.muted, padXMm: px2mm(10) },
                ...Array.from({ length: n }, (_, colIndex) => {
                    if (rowIndex === colIndex) {
                        return { widthMm: PLAYER_COL_W, align: "center", text: "●", color: T.muted, customDraw: (pdf2, cx, cyy, w) => {
                            pdf2.setFillColor(T.border);
                            pdf2.rect(cx, cyy, w, ROW_H, "F");
                            pdf2.setFont(pdf2.__pdfFontName || "PTSans", "normal");
                            pdf2.setFontSize(BODY_FS);
                            pdf2.setTextColor(T.muted);
                            pdf2.text("●", cx + w / 2, cyy + ROW_H / 2 + BODY_FS * 0.352778 * 0.32, { align: "center" });
                        } };
                    }
                    const match = getMatch(group, rowIndex, colIndex);
                    if (!match || !match.sets?.length) {
                        return { widthMm: PLAYER_COL_W, align: "center", text: "-", color: T.muted };
                    }
                    const scoreStr = match.sets.map(set => {
                        const g1 = set.game_score_1 != null ? set.game_score_1 : 0;
                        const g2 = set.game_score_2 != null ? set.game_score_2 : 0;
                        return rowIndex < colIndex ? `${g1}:${g2}` : `${g2}:${g1}`;
                    }).join(" ");
                    const isWin = match.winner_side === (rowIndex < colIndex ? 1 : 2);
                    // No green in the PDF - winner reads as black/bold instead of colored.
                    return { widthMm: PLAYER_COL_W, align: "center", text: scoreStr || "-", color: isWin ? T.ink : T.muted, bold: isWin, fontSizePt: SCORE_FS };
                }),
                { widthMm: DIFF_W, align: "center", text: diff, color: T.ink },
                { widthMm: PTS_W, align: "center", text: ptsVal, color: T.ink },
                { widthMm: PL_W, align: "center", text: place, color: T.ink, customDraw: place !== "—" ? (pdf2, cx, cyy, w) => {
                    pdf2.setFillColor(T.superWhite);
                    pdf2.rect(cx, cyy, w, ROW_H, "F");
                    pdf2.setFont(pdf2.__pdfFontName || "PTSans", "normal");
                    pdf2.setFontSize(BODY_FS);
                    pdf2.setTextColor(T.ink);
                    pdf2.text(String(place), cx + w / 2, cyy + ROW_H / 2 + BODY_FS * 0.352778 * 0.32, { align: "center" });
                } : undefined },
            ];

            drawTableRow(pdf, { x, y: cy, totalWidthMm: contentWidthMm, cols, rowHeightMm: ROW_H, bg: rowIndex % 2 === 0 ? T.superWhite : T.white, borderColor: T.border, fontSizePt: BODY_FS, fontName: pdf.__pdfFontName, textColor: T.ink });
            cy += ROW_H;
        }

        pdf.setDrawColor(T.border);
        pdf.setLineWidth(0.15);
        pdf.roundedRect(x, cardTop, contentWidthMm, cy - cardTop, CARD_RADIUS, CARD_RADIUS, "S");
    });

    return cy;
};
